/**
 * La landing de invitación — el momento de máxima intención del producto.
 *
 * Quien abre este link no instaló nada, no tiene cuenta y probablemente
 * está en el navegador de WhatsApp. Es la única pantalla donde se juega
 * todo (`docs/16` §1.4), y sus cinco reglas duras son estructurales:
 *
 * 1. La cara de quien invita va primero y grande. Nadie acepta una
 *    invitación a una app; acepta una invitación de Ana.
 * 2. El vacío se nombra, no se disimula.
 * 3. Un solo CTA. Sin "conoce más", sin carrusel, sin video.
 * 4. No se pide nada —ni correo, ni ubicación, ni instalación— antes del tap.
 * 5. Nunca es un callejón: un pase vencido ofrece pedirle otro al invitador.
 *
 * Sin ordinal de cortesía (ADR-011): en GU hay un solo número y cuesta
 * diez visitas físicas. Regalar uno en la puerta enseña que aquí los
 * números se regalan.
 */

import { useEffect, useState } from 'react';

import { ApiError, checkInvite, trackEvent, type InviteCheck } from '../../shared/lib/api.js';
import { Boton } from '../../shared/ui/primitives.js';

export interface InviteLandingProps {
  code: string;
  /** El tap explícito. Es lo ÚNICO que fija el pase (spec §4). */
  onAccept: (code: string) => void;
}

type Estado =
  | { fase: 'cargando' }
  | { fase: 'listo'; datos: InviteCheck }
  | { fase: 'desconocido' };

export function InviteLanding({ code, onAccept }: InviteLandingProps) {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });

  useEffect(() => {
    let vivo = true;

    // El peldaño de arriba del embudo. El delta contra `invite_check_ok`
    // es donde cae el webview de WhatsApp (spec §16.6).
    void trackEvent('invite_landing_view', code);

    void checkInvite(code)
      .then((datos) => {
        if (vivo) setEstado({ fase: 'listo', datos });
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        // Un 404 es "ese código no existe"; cualquier otro fallo también
        // acaba aquí, porque para el usuario la acción es la misma.
        if (e instanceof ApiError) setEstado({ fase: 'desconocido' });
        else setEstado({ fase: 'desconocido' });
      });

    return () => {
      vivo = false;
    };
  }, [code]);

  if (estado.fase === 'cargando') {
    // Sin esqueleto: un armazón de datos falsos en la pantalla que decide
    // el embudo entero se lee como contenido y luego cambia bajo el dedo.
    return (
      <Pantalla>
        <p style={{ color: 'var(--gu-text-secondary)', margin: 0 }}>Un momento…</p>
      </Pantalla>
    );
  }

  if (estado.fase === 'desconocido') {
    return (
      <Pantalla>
        <Titulo>No reconocemos ese pase.</Titulo>
        <Parrafo>
          Puede que el link se haya cortado al copiarlo. Pídele a quien te
          invitó que te lo mande otra vez.
        </Parrafo>
      </Pantalla>
    );
  }

  const { datos } = estado;
  const quien = datos.inviter?.displayName ?? '';

  if (datos.state !== 'valid') {
    return <PaseGastado state={datos.state} quien={quien} />;
  }

  return (
    <Pantalla>
      {datos.inviter ? (
        <header
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--gu-space-3)',
          }}
        >
          {datos.inviter.avatarUrl && (
            <img
              src={datos.inviter.avatarUrl}
              alt={`Foto de ${quien}`}
              width={96}
              height={96}
              style={{ borderRadius: '50%', display: 'block' }}
            />
          )}
          <h1
            style={{
              fontSize: 'var(--gu-text-h1)',
              lineHeight: 'var(--gu-leading-tight)',
              margin: 0,
              textAlign: 'center',
            }}
          >
            {quien} te está invitando.
          </h1>
        </header>
      ) : (
        <Titulo>Te están invitando a Gourmet Urbano.</Titulo>
      )}

      <Separador />

      <Parrafo>
        Gourmet Urbano es un mapa de comida que apenas empezó.
      </Parrafo>

      {/*
       * Variante día cero (`docs/16` §1.4). Semilla Cero no tiene ni
       * invitador ni lugares que enseñar, y disimularlo sería prometer
       * contenido que no existe.
       */}
      {!datos.inviter && (
        <Parrafo>
          <strong>Todavía no hay nada adentro. Ni un solo lugar.</strong> Ese es
          el punto: lo que haya dentro de un año, lo pusimos nosotros esta
          semana.
        </Parrafo>
      )}

      <Separador />

      <Parrafo>
        <strong>Aquí no vas a encontrar dónde comer. Todavía no.</strong> Vas a
        escribir dónde comieron los demás.
      </Parrafo>

      <div>
        <h2 style={{ fontSize: 'var(--gu-text-body)', margin: `0 0 var(--gu-space-2)` }}>
          Cómo funciona
        </h2>
        <ol
          style={{
            margin: 0,
            paddingInlineStart: 'var(--gu-space-5)',
            color: 'var(--gu-text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--gu-space-1)',
          }}
        >
          <li>Solo se entra por invitación.</li>
          <li>Solo opinas de un lugar si estás parado ahí. Sin excepciones.</li>
          <li>
            Los primeros 500 que suban 10 lugares se quedan con un número que ya
            no se vuelve a dar.
          </li>
        </ol>
      </div>

      <Separador />

      {/*
       * La escasez vive aquí: en el pase y su fecha, no en un ordinal
       * regalado (ADR-011). "Es tuyo hasta el 27 de agosto."
       */}
      <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
        Este pase es tuyo hasta el <strong>{fechaLarga(datos.expiresAt)}</strong>.
      </p>

      <Boton onClick={() => onAccept(code)}>Entrar</Boton>
    </Pantalla>
  );
}

/**
 * Pase vencido o ya usado. **Nunca un callejón** (`docs/03` §4.1): la
 * recuperación de una invitación vencida vale más que cualquier campaña
 * de adquisición, así que la pantalla dice a quién pedirle otro y abre
 * WhatsApp con el mensaje escrito.
 */
function PaseGastado({
  state,
  quien,
}: {
  state: 'expired' | 'redeemed';
  quien: string;
}) {
  const vencido = state === 'expired';
  const aQuien = quien || 'quien te invitó';

  const mensaje = vencido
    ? `Hola, se me venció el pase de Gourmet Urbano que me mandaste. ¿Me pasas otro?`
    : `Hola, el pase de Gourmet Urbano que me mandaste ya estaba usado. ¿Me pasas otro?`;

  return (
    <Pantalla>
      <Titulo>
        {vencido ? 'Este pase ya se venció.' : 'Alguien ya entró con este pase.'}
      </Titulo>
      <Parrafo>
        {vencido ? (
          <>Pídele otro a {aQuien} — se le repone solo.</>
        ) : (
          <>Si no fuiste tú, pídele otro a {aQuien} — se le repone solo.</>
        )}
      </Parrafo>

      <a
        href={`https://wa.me/?text=${encodeURIComponent(mensaje)}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'var(--gu-touch-comfortable)',
          borderRadius: 'var(--gu-radius-md)',
          background: 'var(--gu-primary-strong)',
          color: 'var(--gu-white)',
          textDecoration: 'none',
          fontWeight: 550,
        }}
      >
        Avisarle por WhatsApp
      </a>
    </Pantalla>
  );
}

/** "27 de agosto" — sin año: el pase dura 14 días, el año sobra. */
function fechaLarga(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
  });
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--gu-surface-app)',
        padding: 'var(--gu-space-6) var(--gu-space-5) var(--gu-space-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gu-space-4)',
        maxWidth: '32rem',
        marginInline: 'auto',
      }}
    >
      {children}
    </main>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <h1
      style={{
        fontSize: 'var(--gu-text-h1)',
        lineHeight: 'var(--gu-leading-tight)',
        margin: 0,
      }}
    >
      {children}
    </h1>
  );
}

function Parrafo({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>{children}</p>
  );
}

function Separador() {
  return (
    <hr
      style={{
        border: 0,
        borderTop: '1px solid var(--gu-border-subtle)',
        width: '100%',
        margin: 0,
      }}
    />
  );
}

